import { BorderOuterOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, message, Modal, Table } from 'antd';
import React, { useState } from 'react';
import { fetchAPI } from '../../utils/api';
import { FIVE_MINUTES } from '../../constants/CONSTANTS';
import { queryClient } from '../../QueryClient';
import ReactJson from 'react-json-view';
import { formatUTCTimeToLocal } from '../../utils/format';
import { ExternalOrder } from './OrderModalMapExternalOrder';

const OrderEmailOrderPicker = (props: {
  record: { id: number; payment_status: string };
  refreshList?: () => Promise<void>;
}) => {
  const [filters, setFilters] = useState({
    page: 1,
    q: '',
  });
  const [visible, setVisible] = useState(false);
  const [selectedExternalOrderId, setSelectedExternalOrderId] = useState(null);
  const [saving, setSaving] = useState(false);

  const externalOrders = useQuery<{
    items: ExternalOrder[];
    page: number;
    per_page: number;
    total: number;
    loading: boolean;
  }>(
    ['external-orders-on-modal', filters, visible],
    async () => {
      if (!visible) {
        return {
          items: [],
          page: filters.page,
          per_page: 5,
          total: 0,
          loading: false,
        };
      }
      try {
        const resp = await fetchAPI('order/external/list', {
          method: 'POST',
          payload: {
            ...filters,
            per_page: 5,
            filters: {
              record_type: 'REMITTANCE',
            },
          },
        });

        return resp;
      } catch (error) {
        return {
          loading: false,
          items: [],
          page: filters.page,
          per_page: 5,
          total: 0,
        };
      }
    },
    {
      staleTime: FIVE_MINUTES,
      placeholderData: {
        items: [],
        loading: true,
        page: filters.page,
        per_page: 5,
        total: 0,
      },
    }
  );

  const saveSelectedExternalOrder = async () => {
    if (!selectedExternalOrderId) {
      return;
    }

    setSaving(true);
    try {
      await fetchAPI(`order/external/update/${selectedExternalOrderId}`, {
        method: 'PATCH',
        payload: {
          order_id: props.record.id,
        },
      });
      message.success('Updated!');
      setVisible(false);
      props.refreshList();
    } catch (error) {
      message.error('Failed update');
      if (error.message) {
        message.error(error.message);
      }
    }
    setSaving(false);
  };

  return (
    <>
      <Button
        style={{ marginTop: 10, fontSize: 12 }}
        size="small"
        icon={<BorderOuterOutlined />}
        onClick={() => {
          setVisible(true);
          setFilters({ page: 1, q: '' });
          setSelectedExternalOrderId(null);
          setSaving(false);
          queryClient.invalidateQueries(['external-orders-on-modal']);
        }}
      >
        Select Email Reader's Order
      </Button>
      <Modal
        title="Select Order from Email Reader"
        visible={visible}
        onCancel={() => {
          if (!saving) {
            setVisible(false);
          }
        }}
        width={1000}
        okButtonProps={{ disabled: !selectedExternalOrderId, loading: saving }}
        cancelButtonProps={{ disabled: saving }}
        onOk={() => saveSelectedExternalOrder()}
      >
        <Input.Search
          enterButton
          placeholder="Search External Order"
          allowClear
          onSearch={value => {
            setFilters({ q: value || '', page: 1 });
          }}
        />
        <Table
          dataSource={externalOrders.data.items}
          loading={externalOrders.data.loading || externalOrders.isLoading}
          pagination={{
            size: 'small',
            showSizeChanger: false,
            current: filters.page,
            pageSize: 5,
            total: externalOrders.data.total,
            onChange(page) {
              setFilters(fils => ({ ...fils, page }));
            },
          }}
          style={{ marginTop: 15 }}
          scroll={{
            x: 1750,
          }}
          columns={[
            { title: 'ID', key: 'id', dataIndex: 'id', fixed: true, width: 75 },
            {
              title: 'Order ID',
              key: 'order-id',
              width: 132,
              fixed: true,
              render: (t, r) => {
                const selected = selectedExternalOrderId === r.id;
                return (
                  <div>
                    <p style={{ marginBottom: 10 }}>
                      Order ID: {r.order_id || '-'}
                    </p>
                    <Button
                      type={selected ? 'primary' : 'default'}
                      size="small"
                      style={{ fontSize: 12 }}
                      onClick={() =>
                        setSelectedExternalOrderId(selected ? null : r.id)
                      }
                      disabled={saving}
                    >
                      {selected ? 'Deselect' : 'Select'}
                    </Button>
                  </div>
                );
              },
            },
            {
              title: 'Source Email',
              key: 'source-email',
              dataIndex: 'source_email',
              width: 250,
            },
            {
              title: 'Source Subject',
              key: 'source-subject',
              dataIndex: 'source_subject',
              width: 250,
            },
            {
              title: 'Invoice No.',
              key: 'invoice-no',
              dataIndex: 'invoice_no',
              width: 250,
            },
            {
              title: 'Invoice Total',
              key: 'invoice_total',
              dataIndex: 'invoice_total',
              width: 250,
            },
            {
              title: 'Source Type',
              key: 'source-type',
              dataIndex: 'source_type',
              width: 250,
            },
            {
              title: 'Created at',
              key: 'created-at',
              render: (t, r) => {
                if (!r.created_at) {
                  return '-';
                }

                return formatUTCTimeToLocal(r.created_at, 'HH:mm, DD MMM YYYY');
              },
              width: 250,
            },
            {
              title: 'PO Payload',
              key: 'po-payload',
              render: (t, r) => {
                return (
                  <ReactJson
                    name="po_payload"
                    style={{ marginTop: 10 }}
                    src={r.po_payload || {}}
                    displayDataTypes={false}
                    indentWidth={2}
                    collapsed
                  />
                );
              },
              width: 250,
            },
          ]}
        />
      </Modal>
    </>
  );
};

export default OrderEmailOrderPicker;
